import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Mail, Phone, HelpCircle, Clock, FileText, CreditCard, AlertCircle } from 'lucide-react';
import { SEO } from '../components/SEO';

const CustomerServicePage = () => {
  const faqItems = [
    {
      question: "How do I cancel my subscription?",
      answer: "Go to Dashboard > Settings > Subscription and click 'Cancel Subscription'. You'll retain access until the end of your billing period."
    },
    {
      question: "How do creators get paid?",
      answer: "Creators receive payments monthly via Stripe Connect. Minimum payout is $10. Set up your payout details in Dashboard > Earnings."
    },
    {
      question: "Can I get a refund?",
      answer: "Refunds are available within 7 days of purchase for unused subscriptions. Contact us with your order details."
    },
    {
      question: "How do I report inappropriate content?",
      answer: "Click the flag icon on any comic or comment, or contact us directly. We review all reports within 24 hours."
    },
    {
      question: "Why was my comic removed?",
      answer: "Comics may be removed for violating our content guidelines. Check your email for details and contact support to appeal."
    }
  ];

  return (
    <div className="min-h-screen bg-[var(--background)]" data-testid="customer-service-page">
      <SEO title="Customer Service" description="Get help with your Supremics account, subscriptions, and more." />
      
      {/* Hero */}
      <div className="relative bg-gradient-to-b from-[var(--surface)] to-[var(--background)] border-b border-[var(--border)]">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/3 w-96 h-96 bg-[var(--primary)]/5 rounded-full blur-3xl" />
        </div>
        <div className="relative page-padding py-16 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <HelpCircle className="text-[var(--primary)]" size={40} />
          </div>
          <h1 className="text-4xl md:text-5xl font-teko font-semibold mb-4">Customer Service</h1>
          <p className="text-[var(--text-secondary)] max-w-xl mx-auto">
            We're here to help. Find answers or get in touch with our support team.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="page-padding py-12">
        <div className="max-w-6xl mx-auto">
          
          {/* Contact Options */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-6 text-center hover:border-[var(--primary)]/50 transition-colors">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[var(--primary)]/10 flex items-center justify-center">
                <Mail className="text-[var(--primary)]" size={24} />
              </div>
              <h3 className="font-teko text-xl mb-2">Email Support</h3>
              <p className="text-[var(--text-secondary)] text-sm mb-4">Get a response within 24-48 hours</p>
              <a href="mailto:support@supremics.com" className="text-[var(--primary)] hover:underline">
                support@supremics.com
              </a>
            </div>
            
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-6 text-center hover:border-[var(--secondary)]/50 transition-colors">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[var(--secondary)]/10 flex items-center justify-center">
                <MessageCircle className="text-[var(--secondary)]" size={24} />
              </div>
              <h3 className="font-teko text-xl mb-2">Live Chat</h3>
              <p className="text-[var(--text-secondary)] text-sm mb-4">Available Mon-Fri, 9am-6pm PST</p>
              <button className="text-[var(--secondary)] hover:underline">
                Start Chat
              </button>
            </div>
            
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-6 text-center hover:border-green-500/50 transition-colors">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                <Phone className="text-green-500" size={24} />
              </div>
              <h3 className="font-teko text-xl mb-2">Phone Support</h3>
              <p className="text-[var(--text-secondary)] text-sm mb-4">For urgent issues only</p>
              <p className="text-green-500">+1 (555) 123-4567</p>
            </div>
          </div>

          {/* Quick Links */}
          <div className="grid md:grid-cols-4 gap-4 mb-12">
            <Link to="/contact" className="flex items-center gap-3 p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl hover:border-[var(--primary)]/50 transition-colors">
              <FileText size={20} className="text-[var(--primary)]" />
              <span>Contact Form</span>
            </Link>
            <Link to="/privacy" className="flex items-center gap-3 p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl hover:border-[var(--primary)]/50 transition-colors">
              <AlertCircle size={20} className="text-[var(--primary)]" />
              <span>Privacy Policy</span>
            </Link>
            <Link to="/terms" className="flex items-center gap-3 p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl hover:border-[var(--primary)]/50 transition-colors">
              <FileText size={20} className="text-[var(--primary)]" />
              <span>Terms of Service</span>
            </Link>
            <Link to="/pricing" className="flex items-center gap-3 p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl hover:border-[var(--primary)]/50 transition-colors">
              <CreditCard size={20} className="text-[var(--primary)]" />
              <span>Pricing & Plans</span>
            </Link>
          </div>

          {/* FAQ Section */}
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-8">
            <h2 className="text-2xl font-teko flex items-center gap-2 mb-6">
              <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <details key={index} className="group">
                  <summary className="flex items-center justify-between p-4 bg-[var(--background)] rounded-xl cursor-pointer hover:bg-[var(--surface-highlight)] transition-colors">
                    <span className="font-medium">{item.question}</span>
                    <span className="text-[var(--primary)] group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <div className="p-4 text-[var(--text-secondary)]">
                    {item.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>

          {/* Response Times */}
          <div className="mt-8 p-6 bg-[var(--surface)] border border-[var(--border)] rounded-2xl">
            <h3 className="font-teko text-xl mb-4 flex items-center gap-2">
              <Clock size={20} className="text-[var(--primary)]" />
              Expected Response Times
            </h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="flex justify-between p-3 bg-[var(--background)] rounded-lg">
                <span className="text-[var(--text-secondary)]">Email Support</span>
                <span className="font-medium">24-48 hours</span>
              </div>
              <div className="flex justify-between p-3 bg-[var(--background)] rounded-lg">
                <span className="text-[var(--text-secondary)]">Live Chat</span>
                <span className="font-medium">Under 5 minutes</span>
              </div>
              <div className="flex justify-between p-3 bg-[var(--background)] rounded-lg">
                <span className="text-[var(--text-secondary)]">Content Review</span>
                <span className="font-medium">24 hours</span>
              </div>
            </div>
          </div>

          {/* Back Link */}
          <div className="text-center mt-8">
            <Link to="/" className="text-[var(--primary)] hover:underline">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerServicePage;
